/**
 * OAuth Storage Helper - Uses native Preferences for persistence across app restarts
 * Critical for iOS OAuth flows where the app may be terminated by the system
 * while the user is authenticating in Safari.
 */

import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { logger } from '@/lib/logger';

/**
 * Save OAuth data using native storage (iOS UserDefaults / Android SharedPreferences)
 * Falls back to localStorage for web platforms
 */
export const saveOAuthData = async (key: string, value: string): Promise<void> => {
  try {
    const timestamp = new Date().toISOString();
    
    if (Capacitor.isNativePlatform()) {
      await Preferences.set({ key, value });
      logger.info('📱 OAuth data saved to native Preferences', { 
        key, 
        valueLength: value?.length || 0,
        timestamp,
        platform: Capacitor.getPlatform()
      });
    }
    
    // Always save to localStorage as fallback
    localStorage.setItem(key, value);
    
    logger.debug('OAuth data saved', { 
      key, 
      native: Capacitor.isNativePlatform(),
      timestamp 
    });
  } catch (error) {
    logger.error('Failed to save OAuth data', { key, error });
    // Fallback to localStorage only
    localStorage.setItem(key, value);
  }
};

/**
 * Get OAuth data from native storage first, fallback to localStorage
 */
export const getOAuthData = async (key: string): Promise<string | null> => {
  try {
    let value: string | null = null;
    let source = 'none';
    
    // Try native Preferences first on native platforms
    if (Capacitor.isNativePlatform()) {
      const result = await Preferences.get({ key });
      if (result.value) {
        value = result.value;
        source = 'preferences';
        logger.info('✅ OAuth data found in native Preferences', { 
          key, 
          valueLength: value?.length || 0,
          platform: Capacitor.getPlatform()
        });
      }
    }
    
    // Fallback to localStorage
    if (!value) {
      value = localStorage.getItem(key);
      if (value) {
        source = 'localStorage';
      }
    }
    
    logger.debug('OAuth data retrieved', { 
      key, 
      found: !!value,
      source,
      valueLength: value?.length || 0
    });
    
    if (!value) {
      logger.warn('⚠️ OAuth data NOT found', { 
        key,
        platform: Capacitor.getPlatform(),
        isNative: Capacitor.isNativePlatform()
      });
    }
    
    return value;
  } catch (error) {
    logger.error('Failed to get OAuth data', { key, error });
    // Fallback to localStorage only
    return localStorage.getItem(key);
  }
};

/**
 * Clear OAuth data from both native storage and localStorage
 */
export const clearOAuthData = async (key: string): Promise<void> => {
  try {
    if (Capacitor.isNativePlatform()) {
      await Preferences.remove({ key });
      logger.debug('OAuth data cleared from native Preferences', { key });
    }
    
    localStorage.removeItem(key);
    logger.debug('OAuth data cleared', { key });
  } catch (error) {
    logger.error('Failed to clear OAuth data', { key, error });
    // Still try to clear localStorage
    localStorage.removeItem(key);
  }
};

/**
 * Clear all Spotify OAuth data
 */
export const clearSpotifyOAuthData = async (): Promise<void> => {
  const keys = [
    'spotify_code_verifier',
    'spotify_state',
    'spotify_redirect_uri',
    'spotify_platform',
    'spotify_auth_method',
    'spotify_auth_attempt'
  ];
  
  logger.info('🧹 Clearing all Spotify OAuth data', { keys });
  
  await Promise.all(keys.map(key => clearOAuthData(key)));
  
  logger.info('✅ All Spotify OAuth data cleared');
};
